import { useMemo, useState } from "react";
import { Eye, ShieldCheck } from "lucide-react";

import emptyDocs from "assets/images/empty-docs.svg";
import { DataTable } from "components/ui/DataTable/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { usePatientDeaspCertificates } from "features/healthlink/hooks/usePatientDeaspCertificates";
import { getDeaspCertificateStatusDisplay } from "features/healthlink/utils/deaspCertificateStatus";
import { formatDate } from "utils/dateFormatter";

import { DeaspDetailsModal } from "./DeaspDetailsModal/DeaspDetailsModal";
import styles from "./DeaspTable.module.css";

const CERTIFICATE_TYPE_LABELS = {
  MED1: "Medical Certificate 1",
  MED2: "Medical Certificate 2",
};

const YES_NO_PILL = {
  Yes: PILL_COLORS.sentYes,
  No: PILL_COLORS.sentNo,
};

const COLUMNS = [
  {
    id: "certFormType",
    header: "Type",
    width: "130px",
    render: (row) =>
      CERTIFICATE_TYPE_LABELS[row.deaspCertificate?.certFormType] ?? "—",
  },
  {
    id: "status",
    header: "Status",
    width: "130px",
    render: (row) => {
      const status = getDeaspCertificateStatusDisplay(row.status);

      if (!status) return "—";

      return <Pill {...status.colors}>{status.label}</Pill>;
    },
  },
  {
    id: "messageControlId",
    header: "Message ID",
    width: "140px",
    render: (row) =>
      row.messageControlId ?? row.deaspCertificate?.messageControlId ?? "—",
    getTooltipText: (row) =>
      row.messageControlId ?? row.deaspCertificate?.messageControlId ?? "—",
  },
  {
    id: "natureOfCondition",
    header: "Nature of Condition",
    width: "minmax(150px, 1fr)",
    render: (row) => {
      if (row.deaspCertificate?.certFormType === "MED2")
        return "Not applicable";
      return row.deaspCertificate?.natureOfCondition ?? "—";
    },
    getTooltipText: (row) => {
      if (row.deaspCertificate?.certFormType === "MED2")
        return "Not applicable";
      return row.deaspCertificate?.natureOfCondition ?? "—";
    },
  },
  {
    id: "sickFromDate",
    header: "Sick From",
    width: "100px",
    render: (row) => formatDate(row.deaspCertificate?.sickFromDate),
  },
  {
    id: "sickToDate",
    header: "Sick To",
    width: "100px",
    render: (row) => formatDate(row.deaspCertificate?.sickToDate),
  },
  {
    id: "finalCertIndicator",
    header: "Final",
    width: "65px",
    render: (row) => {
      const value = row.deaspCertificate?.finalCertIndicator;
      const colors = YES_NO_PILL[value];
      if (!colors) return value ?? "—";
      return <Pill {...colors}>{value}</Pill>;
    },
  },
];

export const DeaspTable = ({ patientId, bare = false }) => {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const { data, isLoading } = usePatientDeaspCertificates({
    patientId,
    page,
    rows,
  });

  const items = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  const rowActions = useMemo(
    () => [
      {
        label: "Details",
        icon: Eye,
        primary: true,
        onClick: (row) => setSelectedCertificate(row),
      },
    ],
    [],
  );

  return (
    <div className={bare ? styles.wrapperBare : styles.wrapper}>
      <div className={styles.header}>
        <p className={styles.title}>
          <ShieldCheck size={20} />
          DEASP Certificates
        </p>
      </div>

      <DataTable
        columns={COLUMNS}
        data={items}
        isLoading={isLoading}
        emptyIllustration={bare ? undefined : emptyDocs}
        emptyTitle="No DEASP certificates found"
        emptyText="There are no DEASP certificates for this patient."
        currentPage={page}
        rowsPerPage={rows}
        totalCount={totalRecords}
        onPageChange={setPage}
        rowsOptions={[10, 20, 50]}
        rowsValue={rows}
        onRowsChange={(val) => {
          setRows(val);
          setPage(1);
        }}
        rowActions={rowActions}
      />

      <DeaspDetailsModal
        certificate={selectedCertificate}
        onClose={() => setSelectedCertificate(null)}
      />
    </div>
  );
};
